// The surveys list (consistency-pass spec §4): an index page over
// `POST /v1/workspaces/{ws}/surveys/search` — free text, Status and Template
// filters through kore's SearchBar and the URL, sort, cursor paging, and a
// row menu for each survey's verbs.

import { Button, buttonVariants, SearchBar, useKoreSearch } from "@pej/kore/components";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { type SurveySearchQueryField, type SurveySortField, useSurveysSearch } from "@/api/surveys";
import { useTemplates } from "@/api/templates";
import { IndexPage } from "@/components/index-page/index-page";
import { PageError, useRetry } from "@/components/page-states";
import type { RawFilters } from "@/features/results/responses-filters";
import { usePageSearch } from "@/features/search/page-search";
import { useCursorStack } from "@/features/search/use-cursor-stack";
import {
    SURVEY_DEFAULT_ORDER,
    SURVEY_FILTER_PARAMS,
    SURVEY_PAGE_SIZE_OPTIONS,
    SURVEY_QUERY_FIELDS,
    SURVEY_SORT_FIELDS,
    surveyFiltersForWire,
    surveysManifest,
    withOneStatus,
} from "@/features/surveys/surveys-filters";
import { SurveysTable } from "@/features/surveys/surveys-table";

export const Route = createFileRoute("/w/$workspaceId/surveys/")({
    component: SurveysPage,
});

function SurveysPage() {
    const { workspaceId } = Route.useParams();
    // Rows and templates load in parallel. Only what names a template waits:
    // the Template cell and the Template chip show nothing until the list is
    // in (never "—", which means no template, nor a raw id).
    const templatesQuery = useTemplates(workspaceId);
    const templatesRetry = useRetry(templatesQuery);
    const templates = templatesQuery.data ?? null;
    const page = usePageSearch<SurveySortField, SurveySearchQueryField>({
        sortFields: SURVEY_SORT_FIELDS,
        defaultOrder: SURVEY_DEFAULT_ORDER,
        queryFields: SURVEY_QUERY_FIELDS,
        pageSizeOptions: SURVEY_PAGE_SIZE_OPTIONS,
    });
    const urlTemplateIds = String(page.rawSearch.template ?? "");
    const manifest = useMemo(
        () => surveysManifest(templates, urlTemplateIds ? urlTemplateIds.split(",") : []),
        [templates, urlTemplateIds],
    );
    // kore reads the URL through withOneStatus: Status is one value or none.
    const router = useMemo(
        () => ({ search: withOneStatus(page.rawSearch), onChange: page.onSearchPatch }),
        [page.rawSearch, page.onSearchPatch],
    );
    const search = useKoreSearch<RawFilters>({
        manifest,
        router,
        orderBy: page.orderBy,
        onOrderByChange: page.onOrderByChange,
        query: page.query,
        onQueryChange: page.setQuery,
    });
    const predicate = page.predicate(surveyFiltersForWire(search.rq?.filters));
    const cursor = useCursorStack({ request: predicate });
    const surveysQuery = useSurveysSearch(
        workspaceId,
        search.ready ? { ...predicate, pageToken: cursor.pageToken } : null,
    );
    const pager = cursor.pager(surveysQuery.data?.nextPageToken ?? null);
    // From the chips on screen, not the wire: whatever kore shows as a chip
    // is what Clear filters clears.
    const filtered = search.filters.length > 0 || page.searchText !== "";
    // A link styled as a button (spec §2): cmd-click opens a tab.
    const createButton = (
        <Link to="/w/$workspaceId/surveys/new" params={{ workspaceId }} className={buttonVariants()}>
            <Plus aria-hidden="true" />
            Create survey
        </Link>
    );

    function clearFilters() {
        page.setQuery("");
        page.onSearchPatch(Object.fromEntries(SURVEY_FILTER_PARAMS.map((key) => [key, undefined])));
    }

    return (
        <IndexPage
            fill
            title="Surveys"
            description="The questions you put to guests — each a short run of steps with its own language, look, and results."
            action={createButton}
        >
            <SearchBar {...search.barProps} size="compact" />
            {templatesRetry.failed && <PageError compact {...templatesRetry.props} />}
            <SurveysTable
                className="min-h-0 flex-1"
                workspaceId={workspaceId}
                surveys={surveysQuery.data?.items ?? []}
                templates={templates}
                isLoading={surveysQuery.isPending}
                error={surveysQuery.isError ? surveysQuery.error : undefined}
                onRetry={() => void surveysQuery.refetch()}
                sort={{ column: page.orderBy.field, direction: page.orderBy.order }}
                onSortChange={(next) =>
                    page.onOrderByChange(next ? { field: next.column, order: next.direction } : null)
                }
                empty={
                    filtered
                        ? {
                              headline: "No surveys match these filters.",
                              body: "Try a different search or clear the filters.",
                              action: (
                                  <Button variant="outline" size="sm" onClick={clearFilters}>
                                      Clear filters
                                  </Button>
                              ),
                          }
                        : {
                              headline: "No surveys yet.",
                              body: "Create a survey to start collecting feedback at your placements.",
                              action: createButton,
                          }
                }
                pagination={{
                    ...pager,
                    pageSize: page.pageSize,
                    totalCount: surveysQuery.data?.totalCount ?? 0,
                    pageSizeOptions: [...SURVEY_PAGE_SIZE_OPTIONS],
                    onPageSizeChange: page.setPageSize,
                    entityLabel: "survey",
                }}
            />
        </IndexPage>
    );
}
